'use client';

import React, { useRef } from 'react';
import { Course } from './LearningHubContent';
import {
  ClockIcon,
  BookOpenIcon,
  UserCircleIcon,
  PlayCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const difficultyConfig = {
  Beginner: 'bg-positive/10 text-positive',
  Intermediate: 'bg-warning/10 text-warning',
  Advanced: 'bg-negative/10 text-negative',
};

interface Props {
  course: Course;
  categoryColors: Record<string, string>;
}

export default function CourseCard({ course, categoryColors }: Props) {
  const lessonDialog = useRef<HTMLDialogElement>(null);
  const isCompleted = course.progress === 100;
  const isStarted = course.progress > 0;

  return (
    <div
      className={`bg-card border rounded-xl p-5 flex flex-col gap-3 hover:border-primary/30 transition-all duration-200 cursor-pointer group ${
        course.featured ? 'border-primary/20 card-glow-amber' : 'border-border'
      }`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${categoryColors[course.category] ?? 'bg-muted text-muted-foreground'}`}>
            {course.category}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${difficultyConfig[course.difficulty]}`}>
            {course.difficulty}
          </span>
        </div>
        {course.featured && (
          <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20 flex-shrink-0">
            Featured
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-sm font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
        {course.title}
      </h3>

      {/* Description */}
      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">
        {course.description}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1">
        {course.tags.slice(0, 3).map((tag) => (
          <span key={`tag-${course.id}-${tag}`} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            {tag}
          </span>
        ))}
      </div>

      {/* Meta */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <ClockIcon style={{ width: 12, height: 12 }} />
          {course.duration}
        </span>
        <span className="flex items-center gap-1">
          <BookOpenIcon style={{ width: 12, height: 12 }} />
          {course.lessons} lessons
        </span>
        <span className="flex items-center gap-1">
          <UserCircleIcon style={{ width: 12, height: 12 }} />
          {course.enrolled.toLocaleString()}
        </span>
      </div>

      {/* Instructor */}
      <p className="text-xs text-muted-foreground">
        by <span className="text-foreground font-medium">{course.instructor}</span>
      </p>

      {/* Progress */}
      {isStarted && (
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Progress</span>
            <span className="font-mono-data text-xs font-bold text-foreground">{course.progress}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isCompleted ? 'bg-positive' : 'bg-primary'}`}
              style={{ width: `${course.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={() => lessonDialog.current?.showModal()}
        className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all duration-150 active:scale-95 ${
          isCompleted
            ? 'bg-positive/10 text-positive border border-positive/20'
            : isStarted
            ? 'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20' :'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
      >
        {isCompleted ? (
          <>
            <CheckCircleIcon style={{ width: 13, height: 13 }} />
            Completed — Review
          </>
        ) : isStarted ? (
          <>
            <PlayCircleIcon style={{ width: 13, height: 13 }} />
            Continue Learning
          </>
        ) : (
          <>
            <PlayCircleIcon style={{ width: 13, height: 13 }} />
            Start Course
          </>
        )}
      </button>
      <dialog ref={lessonDialog} className="w-[calc(100%-2rem)] max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-card text-foreground p-5 backdrop:bg-black/60">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h2 className="font-bold text-lg">{course.title}</h2>
          <button onClick={() => lessonDialog.current?.close()} className="rounded-lg border border-border px-3 py-1 text-sm">Close</button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{course.description}</p>
        {course.lessonLinks?.length ? (
          <>
            <p className="text-xs text-muted-foreground mb-4">A free, self-paced reading course curated by BizzBox. Lessons open on the source website; US regulatory resources describe US rules.</p>
            <ol className="space-y-3">
              {course.lessonLinks.map((lesson, index) => (
                <li key={lesson.url} className="rounded-xl border border-border p-3">
                  <p className="text-xs text-muted-foreground mb-1">Lesson {index + 1} · {lesson.source}</p>
                  <a href={lesson.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary hover:underline">{lesson.title} ↗</a>
                </li>
              ))}
            </ol>
          </>
        ) : <p className="text-sm text-muted-foreground">Lesson materials for this course are not available yet.</p>}
      </dialog>
    </div>
  );
}
